import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabase";
import {
  FaArrowLeft,
  FaArrowRight,
  FaHeart,
  FaBookmark,
  FaShareAlt,
  FaLanguage,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { addToCart, updateCartItemQuantity, getCartItemQuantity } from "../../utils/cartUtils";
import "./ProductDetails.css";

const PRODUCT_MEDIA_BASE_URL =
  "https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/product-media";

// Resolve product image URL for product-media bucket (same as Profile.jsx)
const resolveProductImageUrl = (image, userId) => {
  if (!image) return "/default-product.png";
  if (typeof image === "string" && /^(http|https):\/\//i.test(image)) return image;
  if (typeof image === "string" && image.startsWith("data:")) return image;

  try {
    const filename = typeof image === "string" ? image : String(image);
    return `${PRODUCT_MEDIA_BASE_URL}/${userId}/${filename}`;
  } catch (_) {
    return "/default-product.png";
  }
};

// Helper function to format USD amount
const formatUSD = (amount) => {
  return `$${amount.toFixed(2)}`;
};

// Product Card Component for "More from this shop" section
const ProductCard = ({ product, onClick, winiToUsdRate }) => {
  const [isLiked, setIsLiked] = useState(false);
  
  // Product images are stored as full URLs in the images array
  const images = Array.isArray(product.images) ? product.images : (product.thumbnail_url ? [product.thumbnail_url] : []);
  const firstImage = images[0] || product.thumbnail_url || null;
  const imageUrl = resolveProductImageUrl(firstImage, product.user_id);
  const priceInWini = Number(product.price_in_coins || 0);
  const priceInUsd = winiToUsdRate ? priceInWini * winiToUsdRate : null;

  const toggleLike = (e) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
  };

  return (
    <div className="more-product-card" onClick={() => onClick(product)}>
      <div className="more-product-image-container">
        <img
          src={imageUrl}
          alt={product.name || "Product"}
          className="more-product-image"
          onError={(e) => {
            e.currentTarget.src = "/default-product.png";
          }}
        />
        <button className="more-product-like-btn" onClick={toggleLike}>
          <FaHeart className={isLiked ? 'liked' : ''} />
        </button>
      </div>
      <div className="more-product-info">
        <h4 className="more-product-title">{product.name || "Product"}</h4>
        <p className="more-product-price">
          {priceInUsd !== null ? formatUSD(priceInUsd) : `${priceInWini.toFixed(2)} Wini`}
        </p>
      </div>
    </div>
  );
};

export default function ProductDetails() {
  const { uid, productId } = useParams();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [product, setProduct] = useState(null);
  const [moreProducts, setMoreProducts] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cartQuantity, setCartQuantity] = useState(0);
  const [winiToUsdRate, setWiniToUsdRate] = useState(null);

  // Fetch WiniCoin USD rate
  useEffect(() => {
    const fetchWiniRate = async () => {
      try {
        const { data: appSettings, error: settingsError } = await supabase
          .from("app_settings")
          .select("wini_to_usd_rate")
          .limit(1)
          .single();

        if (!settingsError && appSettings) {
          setWiniToUsdRate(appSettings.wini_to_usd_rate || null);
        }
      } catch (err) {
        console.error("Error fetching Wini to USD rate:", err);
      }
    };

    fetchWiniRate();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        // Fetch user profile (store owner)
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', uid)
          .single();
        
        if (profileError || !profile) {
          console.error('Profile not found:', profileError);
          if (isMounted) navigate("/404");
          return;
        }

        if (!isMounted) return;
        
        // Transform profile data to match expected format
        const userData = {
          display_name: profile.display_name || profile.username || 'Store',
          username: profile.username || 'user',
          organizationName: profile.display_name || profile.username || 'Store',
          name: profile.display_name || profile.username || 'Store',
        };
        setUserData(userData);

        // Fetch the specific product
        const { data: productData, error: productError } = await supabase
          .from('products')
          .select('*')
          .eq('id', productId)
          .eq('user_id', uid)
          .single();
        
        if (productError || !productData) {
          console.error('Product not found:', productError);
          if (isMounted) navigate("/404");
          return;
        }

        if (!isMounted) return;
        setProduct(productData);
        
        // Check if product is in cart and get quantity
        const quantity = getCartItemQuantity(productId);
        setCartQuantity(quantity);

        // Fetch more products from the same shop (excluding current product)
        const { data: allProducts, error: productsError } = await supabase
          .from('products')
          .select('*')
          .eq('user_id', uid)
          .eq('is_active', true)
          .neq('id', productId)
          .order('created_at', { ascending: false })
          .limit(10);

        if (!productsError && allProducts && allProducts.length > 0) {
          // Get 3 random products from the results
          const shuffled = [...allProducts].sort(() => 0.5 - Math.random());
          const randomProducts = shuffled.slice(0, 3);

          if (isMounted) setMoreProducts(randomProducts);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (isMounted) navigate("/error");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [uid, productId, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const nextImage = () => {
    const images = Array.isArray(product?.images) ? product.images : [];
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    const images = Array.isArray(product?.images) ? product.images : [];
    if (images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const toggleLike = () => {
    setIsLiked(!isLiked);
  };

  const toggleSave = () => {
    setIsSaved(!isSaved);
  };

  const handleShare = () => {
    if (!product || !userData) return;
    
    // Generate share URL: https://iwinto.com/@username/productId/shareId/pr
    const username = userData.username || userData.display_name || 'user';
    // Simple random shareId for URL uniqueness (not stored in DB)
    const shareId = Math.random().toString(36).slice(2, 10);
    const shareUrl = `https://iwinto.com/@${encodeURIComponent(username)}/${productId}/${shareId}/pr`;
    
    // Implement share functionality
    if (navigator.share) {
      navigator.share({
        title: product?.name || "Product",
        text: product?.description || "",
        url: shareUrl,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareUrl);
      alert("Link copied to clipboard!");
    }
  };

  const handleLanguageToggle = () => {
    // Implement language toggle functionality
    console.log("Language toggle clicked");
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    // Map Supabase product to cart item shape
    const images = Array.isArray(product.images) ? product.images : (product.thumbnail_url ? [product.thumbnail_url] : []);
    const priceInWini = Number(product.price_in_coins || 0);
    const priceInUsd = winiToUsdRate ? priceInWini * winiToUsdRate : null;
    
    const cartProduct = {
      id: product.id,
      Title: product.name,
      Images: images.length > 0 ? images : ["/default-product.png"],
      Price: priceInUsd || priceInWini,
      VendorId: product.user_id,
    };

    addToCart(cartProduct);
    setCartQuantity(prev => prev + 1);
  };

  const handleQuantityChange = (change) => {
    if (!product) return;
    updateCartItemQuantity(product.id, change);
    setCartQuantity(prev => {
      const newQuantity = prev + change;
      return newQuantity <= 0 ? 0 : newQuantity;
    });
  };

  const handleMoreProductClick = (clickedProduct) => {
    navigate(`/product-details/${uid}/${clickedProduct.id}`);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-animation"></div>
      </div>
    );
  }

  if (!product || !userData) return <div className="not-found">Product not found</div>;

  // Product images are stored as full URLs in the images array
  // Get the user_id from the product (should always be available)
  const productUserId = product.user_id || uid;
  const imagesArray = Array.isArray(product.images) && product.images.length > 0 
    ? product.images 
    : (product.thumbnail_url ? [product.thumbnail_url] : []);
  
  // Resolve all image URLs properly - ensure we use the correct user_id
  const images = imagesArray.length > 0 
    ? imagesArray.map(img => {
        // If image is already a full URL, return it as-is
        if (typeof img === "string" && /^(http|https):\/\//i.test(img)) {
          return img;
        }
        // Otherwise resolve it using the helper
        return resolveProductImageUrl(img, productUserId);
      })
    : ["/default-product.png"];
  
  const storeName = userData.organizationName || userData.display_name || userData.name || userData.username || "Store";
  const priceInWini = Number(product.price_in_coins || 0);
  const priceInUsd = winiToUsdRate ? priceInWini * winiToUsdRate : null;

  return (
    <div className="product-details-page">
      {/* Header */}
      <div className="product-details-header">
        <button className="product-details-back-button" onClick={handleBack}>
          <FaArrowLeft />
        </button>
        
        <h1 className="product-details-store-name">{storeName}</h1>
      </div>

      {/* Main Product Section */}
      <div className="product-details-main">
        {/* Product Media Carousel */}
        <div className="product-media-section">
          <div className="product-media-container">
            <img
              src={images[currentImageIndex] || "/default-product.png"}
              alt={product.name || "Product"}
              className="product-main-image"
              onError={(e) => {
                e.currentTarget.src = "/default-product.png";
              }}
            />
            
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button className="product-nav-btn product-nav-prev" onClick={prevImage}>
                  <FaArrowLeft />
                </button>
                <button className="product-nav-btn product-nav-next" onClick={nextImage}>
                  <FaArrowRight />
                </button>
              </>
            )}
          </div>
          
          {/* Pagination Dots */}
          {images.length > 1 && (
            <div className="product-pagination-dots">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`pagination-dot ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => goToImage(index)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Info and Actions */}
        <div className="product-info-section">
          <div className="product-price">
            {priceInUsd !== null ? (
              <>
                <span className="product-price-usd">{formatUSD(priceInUsd)}</span>
                {/* <span className="product-price-wini"> • {priceInWini.toFixed(2)} Wini</span> */}
              </>
            ) : (
              <span className="product-price-wini">{priceInWini.toFixed(2)} Wini</span>
            )}
          </div>
          
          <div className="product-details-action-buttons">
            <button className="action-btn language-btn" onClick={handleLanguageToggle}>
              <FaLanguage />
            </button>
            <button className="action-btn like-btn" onClick={toggleLike}>
              <FaHeart className={isLiked ? 'liked' : ''} />
            </button>
            <button className="action-btn save-btn" onClick={toggleSave}>
              <FaBookmark className={isSaved ? 'saved' : ''} />
            </button>
            <button className="action-btn share-btn" onClick={handleShare}>
              <FaShareAlt />
            </button>
            
            {/* Add to Cart Button / Quantity Selector */}
            {cartQuantity === 0 ? (
              <button className="action-btn cart-btn" onClick={handleAddToCart}>
                <FaPlus />
              </button>
            ) : (
              <div className="product-details-quantity-selector">
                <button 
                  className="quantity-btn minus" 
                  onClick={() => handleQuantityChange(-1)}
                >
                  <FaMinus />
                </button>
                <span className="quantity-value">{cartQuantity}</span>
                <button 
                  className="quantity-btn plus" 
                  onClick={() => handleQuantityChange(1)}
                >
                  <FaPlus />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Product Title */}
        <div className="product-title-section">
          <h2 className="product-title">{product.name || "Product"}</h2>
          {product.description && (
            <p className="product-description">{product.description}</p>
          )}
        </div>
      </div>

      {/* More from this shop */}
      {moreProducts.length > 0 && (
        <div className="more-from-shop-section">
          <h3 className="more-from-shop-title">MORE FROM THIS SHOP</h3>
          <div className="more-products-grid">
            {moreProducts.map((moreProduct) => (
              <ProductCard
                key={moreProduct.id}
                product={moreProduct}
                onClick={handleMoreProductClick}
                winiToUsdRate={winiToUsdRate}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
